import { Field, Formik, FormikErrors } from 'formik';
import {
	CerebroDrawer,
	CerebroForm,
	CerebroHeading,
	CerebroLabel,
	CerebroMaskedInput,
	CerebroPrimaryButton,
	CerebroText,
	CerebroYesNo,
} from '../shared/components';
import { CreditOutputAnalysis } from '../shared/models';
import { FormikUtils, NumberUtils } from '../shared/utils';
import { Grid } from '@mui/material';
import { useApi } from '../shared/context';
import { useSnackbar } from 'notistack';
import { useMutation, useQueryClient } from 'react-query';
import { QueryKeys } from '../shared/hooks';
import { useParams } from 'react-router-dom';
import { getApiResponseData } from '../shared/utils/axios';
import { CerebroApiEndpoints } from '../shared/utils/api';
import { EmptyGuid } from '../shared/constants';
import { MaxCurrencyLength } from '../shared/utils/platform';

interface HeaderFormProps {
	header: CreditOutputAnalysis | null;
	close: () => void;
}

export const OutputAnalysisHeaderForm: React.FC<HeaderFormProps> = ({ header, close }) => {
	const api = useApi();
	const snackbar = useSnackbar();
	const client = useQueryClient();
	const { guid } = useParams<'guid'>();

	const headerMutation = useMutation(
		async ({ values }: HeaderFormMutationVariables) => {
			const dto = {
				id: values.id,
				guid: values.guid,
				debtCapacity: NumberUtils.ParseDecimal(values.debtCapacity, undefined),
				isProfitLossSectionVisible: values.isProfitLossSectionVisible,
				isCashFlowSectionVisible: values.isCashFlowSectionVisible,
				isBalanceSheetSectionVisible: values.isBalanceSheetSectionVisible,
				isBusinessSectionVisible: values.isBusinessSectionVisible,
				isDoneEditing: values.isDoneEditing,
			};

			const { data, error } = await getApiResponseData<CreditOutputAnalysis>(
				api.post(CerebroApiEndpoints.Matching.OutputAnalysisHeader(guid), dto)
			);

			if (error) {
				throw new Error(error.response?.data);
			}

			return data;
		},
		{
			onSuccess: () => {
				client.invalidateQueries(QueryKeys.Matching.OutputAnalysis(guid));
				client.invalidateQueries(QueryKeys.Deal.Root(guid));
				snackbar.enqueueSnackbar('Updated output analysis.', { variant: 'success' });
				close();
			},
			onError: (error: Error) => {
				snackbar.enqueueSnackbar(error.message, {
					variant: 'error',
				});
			},
			onSettled: (_data, _error, { helpers }) => {
				helpers.setSubmitting(false);
			},
		}
	);

	return (
		<CerebroDrawer open={header != null} onClose={close}>
			<CerebroHeading>Update Output Analysis</CerebroHeading>

			<Formik
				initialValues={{
					id: header?.id ?? 0,
					guid: header?.guid ?? EmptyGuid,
					debtCapacity: NumberUtils.FormatDecimal(header?.debtCapacity),
					isProfitLossSectionVisible: header?.isProfitLossSectionVisible ?? true,
					isCashFlowSectionVisible: header?.isCashFlowSectionVisible ?? true,
					isBalanceSheetSectionVisible: header?.isBalanceSheetSectionVisible ?? true,
					isBusinessSectionVisible: header?.isBusinessSectionVisible ?? true,
					isDoneEditing: false,
				}}
				validate={validateForm}
				onSubmit={FormikUtils.MutateOnSubmit(headerMutation.mutate)}
			>
				{({ isSubmitting, errors, touched, handleSubmit, values }) => (
					<CerebroForm flatIndicator onSubmit={handleSubmit} sx={{ marginTop: 3, paddingRight: 3 }}>
						<Grid
							container
							spacing={3}
							sx={{ marginTop: 3 }}
							justifyContent={{ xs: 'center', md: 'flex-start' }}
						>
							<Grid item xs={11}>
								<CerebroLabel id="debtCapacity">Debt Capacity</CerebroLabel>
								<Field
									formatas="currency"
									as={CerebroMaskedInput}
									maxLength={MaxCurrencyLength}
									id="debtCapacity"
									name="debtCapacity"
									disabled={isSubmitting}
									error={touched.debtCapacity && errors.debtCapacity !== undefined}
								/>
								{errors.debtCapacity && (
									<CerebroText variant="error" sx={{ marginBottom: 4 }}>
										{errors.debtCapacity}
									</CerebroText>
								)}
							</Grid>

							<Grid item xs={11}>
								<CerebroLabel id="isProfitLossSectionVisible">Show Profile Loss Section</CerebroLabel>
								<Field
									id="isProfitLossSectionVisible"
									as={CerebroYesNo}
									name="isProfitLossSectionVisible"
									disabled={isSubmitting}
									error={
										touched.isProfitLossSectionVisible &&
										errors.isProfitLossSectionVisible !== undefined
									}
								/>
								{errors.debtCapacity && (
									<CerebroText variant="error" sx={{ marginBottom: 4 }}>
										{errors.debtCapacity}
									</CerebroText>
								)}
							</Grid>
							<Grid item xs={11}>
								<CerebroLabel id="isCashFlowSectionVisible">Show Cash Flow Section</CerebroLabel>
								<Field
									id="isCashFlowSectionVisible"
									as={CerebroYesNo}
									name="isCashFlowSectionVisible"
									disabled={isSubmitting}
									error={
										touched.isCashFlowSectionVisible &&
										errors.isCashFlowSectionVisible !== undefined
									}
								/>
								{errors.isCashFlowSectionVisible && (
									<CerebroText variant="error" sx={{ marginBottom: 4 }}>
										{errors.isCashFlowSectionVisible}
									</CerebroText>
								)}
							</Grid>
							<Grid item xs={11}>
								<CerebroLabel id="isBalanceSheetSectionVisible">
									Show Balance Sheet Section
								</CerebroLabel>
								<Field
									id="isBalanceSheetSectionVisible"
									as={CerebroYesNo}
									name="isBalanceSheetSectionVisible"
									disabled={isSubmitting}
									error={
										touched.isBalanceSheetSectionVisible &&
										errors.isBalanceSheetSectionVisible !== undefined
									}
								/>
								{errors.isBalanceSheetSectionVisible && (
									<CerebroText variant="error" sx={{ marginBottom: 4 }}>
										{errors.isBalanceSheetSectionVisible}
									</CerebroText>
								)}
							</Grid>
							<Grid item xs={11}>
								<CerebroLabel id="isBusinessSectionVisible">Show Business Section</CerebroLabel>
								<Field
									id="isBusinessSectionVisible"
									as={CerebroYesNo}
									name="isBusinessSectionVisible"
									disabled={isSubmitting}
									error={
										touched.isBusinessSectionVisible &&
										errors.isBusinessSectionVisible !== undefined
									}
								/>
								{errors.isBusinessSectionVisible && (
									<CerebroText variant="error" sx={{ marginBottom: 4 }}>
										{errors.isBusinessSectionVisible}
									</CerebroText>
								)}
							</Grid>

							<Grid item xs={11}>
								<CerebroLabel id="isDoneEditing">Done Editing Credit Analysis?</CerebroLabel>
								<Field
									id="isDoneEditing"
									as={CerebroYesNo}
									name="isDoneEditing"
									disabled={isSubmitting}
								/>
								<CerebroText variant="hint">
									Set to Yes if this is the last change you are making and the deal setting will be
									updated to turn off edit mode for this page.
								</CerebroText>
							</Grid>

							<Grid item xs={8}>
								<CerebroPrimaryButton loading={isSubmitting} type="submit">
									Save
								</CerebroPrimaryButton>
							</Grid>
						</Grid>
					</CerebroForm>
				)}
			</Formik>
		</CerebroDrawer>
	);
};

type HeaderFormMutationVariables = FormikUtils.MutateOnSubmitVariables<HeaderFormValues>;
interface HeaderFormValues {
	id: number;
	guid: string;
	debtCapacity: string;
	isProfitLossSectionVisible: boolean;
	isCashFlowSectionVisible: boolean;
	isBalanceSheetSectionVisible: boolean;
	isBusinessSectionVisible: boolean;
	isDoneEditing: boolean;
}
type HeaderFormValueErrors = FormikErrors<HeaderFormValues>;

function validateForm(values: HeaderFormValues) {
	const errors: HeaderFormValueErrors = {};

	if (
		!values.isProfitLossSectionVisible &&
		!values.isCashFlowSectionVisible &&
		!values.isBalanceSheetSectionVisible &&
		!values.isBusinessSectionVisible
	) {
		errors.isProfitLossSectionVisible = 'At least one section must be shown.';
		errors.isCashFlowSectionVisible = 'At least one section must be shown.';
		errors.isBalanceSheetSectionVisible = 'At least one section must be shown.';
		errors.isBusinessSectionVisible = 'At least one section must be shown.';
	}

	return errors;
}
